import type {
  AnalyticsService,
  CrashAttrs,
  CrashReporter,
  PerfService,
  PerfTrace,
} from '@mendikot/app-core';
import { noopPerf } from '@mendikot/app-core';
import {
  PERF_TRACES,
  type AnalyticsEventName,
  type AnalyticsEvents,
  type UserProperties,
} from '@mendikot/protocol';
import type { FirebaseApp } from 'firebase/app';
import type { Analytics } from 'firebase/analytics';
import type { FirebasePerformance } from 'firebase/performance';

/**
 * Analytics with consent gating (DPDP): events are buffered until the user decides; on "deny" they
 * are dropped and collection stays off. The Firebase SDK is loaded lazily to keep the boot bundle small.
 */
export class WebAnalytics implements AnalyticsService {
  private analytics: Analytics | null = null;
  private sdk: typeof import('firebase/analytics') | null = null;
  private consent: boolean | null = null;
  private buffer: [string, Record<string, unknown>][] = [];
  private userId: string | null = null;
  private props: Partial<UserProperties> = {};

  constructor(private readonly app: FirebaseApp | null) {}

  setConsent(granted: boolean): void {
    this.consent = granted;
    if (!granted) {
      this.buffer = [];
      if (this.sdk && this.analytics) this.sdk.setAnalyticsCollectionEnabled(this.analytics, false);
      return;
    }
    void this.load();
  }

  log<K extends AnalyticsEventName>(name: K, params: AnalyticsEvents[K]): void {
    if (import.meta.env.DEV) console.debug('[analytics]', name, params);
    if (this.consent === false) return;
    if (!this.analytics || !this.sdk) {
      if (this.buffer.length < 200) this.buffer.push([name, params as Record<string, unknown>]);
      return;
    }
    this.sdk.logEvent(this.analytics, name as string, params as Record<string, unknown>);
  }

  setUserId(uid: string | null): void {
    this.userId = uid;
    if (this.analytics && this.sdk) this.sdk.setUserId(this.analytics, uid);
  }

  setUserProperties(props: Partial<UserProperties>): void {
    this.props = { ...this.props, ...props };
    if (this.analytics && this.sdk) this.sdk.setUserProperties(this.analytics, props);
  }

  private async load(): Promise<void> {
    if (!this.app || this.analytics) return;
    const sdk = await import('firebase/analytics');
    if (!(await sdk.isSupported())) return;
    sdk.setConsent({
      analytics_storage: 'granted',
      ad_storage: 'denied',
      ad_user_data: 'denied',
      ad_personalization: 'denied',
    });
    this.sdk = sdk;
    // Development sessions show up live in the console's DebugView (Analytics → DebugView).
    this.analytics = import.meta.env.DEV
      ? sdk.initializeAnalytics(this.app, { config: { debug_mode: true } })
      : sdk.getAnalytics(this.app);
    sdk.setAnalyticsCollectionEnabled(this.analytics, true);
    if (this.userId) sdk.setUserId(this.analytics, this.userId);
    sdk.setUserProperties(this.analytics, this.props);
    for (const [name, params] of this.buffer) sdk.logEvent(this.analytics, name, params);
    this.buffer = [];
  }
}

const ATTR_NAME = /^[A-Za-z_]{1,32}$/;

/** Firebase Performance custom traces (≤ 5 attributes each, plan §5.3). */
export class WebPerf implements PerfService {
  private perf: FirebasePerformance | null = null;
  private sdk: typeof import('firebase/performance') | null = null;

  constructor(private readonly app: FirebaseApp | null) {}

  async enable(enabled: boolean): Promise<void> {
    if (!this.app) return;
    if (!enabled) {
      if (this.perf) this.perf.dataCollectionEnabled = false;
      return;
    }
    this.sdk ??= await import('firebase/performance');
    this.perf ??= this.sdk.getPerformance(this.app);
    this.perf.dataCollectionEnabled = true;
    this.perf.instrumentationEnabled = true;
  }

  trace(name: keyof typeof PERF_TRACES): PerfTrace {
    if (!this.perf || !this.sdk) return noopPerf.trace(name);
    const allowed = new Set<string>(PERF_TRACES[name]);
    const trace = this.sdk.trace(this.perf, name);
    trace.start();
    let stopped = false;
    return {
      putAttribute: (attr, value) => {
        if (allowed.has(attr) && ATTR_NAME.test(attr))
          trace.putAttribute(attr, value.slice(0, 100));
      },
      putMetric: (metric, value) => trace.putMetric(metric, Math.round(value)),
      incrementMetric: (metric, by = 1) => trace.incrementMetric(metric, by),
      stop: () => {
        if (stopped) return;
        stopped = true;
        trace.stop();
      },
    };
  }
}

const env = import.meta.env;
type SentrySdk = typeof import('@sentry/react');

/**
 * Web crash reporting via Sentry (Crashlytics has no web SDK). The SDK is loaded lazily, only with a
 * DSN and the user's consent; Session Replay records on error only, with all text and media masked.
 * Context and breadcrumbs set before the SDK loads are kept and applied once it is ready.
 */
export class SentryCrashReporter implements CrashReporter {
  private sdk: SentrySdk | null = null;
  private loading = false;
  private user: string | null = null;
  private tags: Record<string, string> = {};
  private crumbs: { category: string; message: string; data?: Record<string, unknown> }[] = [];

  /** Consent withdrawn: nothing more is sent from this session (start() can resume it). */
  stop(): void {
    this.loading = false;
    const sdk = this.sdk;
    this.sdk = null;
    if (!sdk) return;
    // close() only disables the client after its flush settles, which a pending replay upload can
    // delay; switch it off now so nothing captured after withdrawal is sent.
    const client = sdk.getClient();
    if (client) client.getOptions().enabled = false;
    void sdk.getReplay()?.stop();
    void sdk.close(2_000);
  }

  start(appVersion: string): void {
    if (this.loading || this.sdk || !env.VITE_SENTRY_DSN) return;
    this.loading = true;
    void import('@sentry/react').then((Sentry) => {
      if (!this.loading) return; // stopped while the SDK was loading
      this.loading = false;
      Sentry.init({
        dsn: env.VITE_SENTRY_DSN,
        release: `mendikot-web@${appVersion}`,
        environment: env.VITE_SENTRY_ENVIRONMENT ?? env.MODE,
        integrations: [
          Sentry.browserTracingIntegration(),
          Sentry.replayIntegration({ maskAllText: true, blockAllMedia: true }),
        ],
        tracesSampleRate: env.PROD ? 0.1 : 1,
        replaysSessionSampleRate: 0,
        replaysOnErrorSampleRate: 1,
      });
      this.sdk = Sentry;
      Sentry.setUser(this.user ? { id: this.user } : null);
      Sentry.setTags(this.tags);
      for (const c of this.crumbs) Sentry.addBreadcrumb({ ...c, level: 'info' });
      this.crumbs = [];
    });
  }

  setUser(uid: string | null): void {
    this.user = uid;
    this.sdk?.setUser(uid ? { id: uid } : null);
  }

  setContext(attrs: CrashAttrs): void {
    const tags = stringify(attrs);
    Object.assign(this.tags, tags);
    this.sdk?.setTags(tags);
  }

  breadcrumb(category: string, message: string, data?: Record<string, unknown>): void {
    if (this.sdk) this.sdk.addBreadcrumb({ category, message, data, level: 'info' });
    else if (this.crumbs.length < 50) this.crumbs.push({ category, message, data });
  }

  captureError(error: unknown, attrs?: CrashAttrs): void {
    if (env.DEV) console.error(error, attrs);
    this.sdk?.captureException(error, attrs ? { tags: stringify(attrs) } : undefined);
  }

  captureMessage(message: string, attrs?: CrashAttrs): void {
    this.sdk?.captureMessage(message, attrs ? { tags: stringify(attrs) } : undefined);
  }
}

function stringify(attrs: CrashAttrs): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(attrs)) if (v !== null) out[k] = String(v);
  return out;
}
